import React, { useState, useEffect } from 'react';
import { Badge } from './BaseComponents';
import { NetworkStatusIndicatorProps } from '../types';

/**
 * NetworkStatusIndicator Component
 * 
 * Displays the current network connectivity status to users during quiz sessions.
 * Shows different states for online, offline, and connection recovery.
 */
export const NetworkStatusIndicator: React.FC<NetworkStatusIndicatorProps> = ({ 
  className = '',
  showWhenOnline = false 
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [showRecoveryMessage, setShowRecoveryMessage] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      const previouslyOffline = !isOnline;
      setIsOnline(true);
      
      if (previouslyOffline) {
        setWasOffline(true);
        setShowRecoveryMessage(true);
        
        // Hide recovery message after 3 seconds
        setTimeout(() => {
          setShowRecoveryMessage(false);
          setWasOffline(false);
        }, 3000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowRecoveryMessage(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOnline]);

  // Don't show anything if online and showWhenOnline is false
  if (isOnline && !showWhenOnline && !showRecoveryMessage) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {!isOnline && (
        <Badge variant="error" className="animate-pulse">
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
            </svg>
            <span>Offline</span>
          </div>
        </Badge>
      )}
      
      {showRecoveryMessage && (
        <Badge variant="success" className="animate-bounce">
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Back Online</span>
          </div>
        </Badge>
      )}
      
      {isOnline && showWhenOnline && !showRecoveryMessage && (
        <Badge variant="success">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Online</span>
          </div>
        </Badge>
      )}
    </div>
  );
};

export default NetworkStatusIndicator;